import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Utensils } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../services/api';

interface Table {
    _id: string;
    tableNumber: string;
    restaurant: {
        _id: string;
        name: string;
        nameAr?: string;
        logo: string;
    };
    status: string;
}

const TableLandingPage: React.FC = () => {
    const { qrCode } = useParams<{ qrCode: string }>();
    const navigate = useNavigate();
    const { t, language, setLanguage, isRTL } = useLanguage();

    const [table, setTable] = useState<Table | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (qrCode) {
            fetchTableInfo();
        }
    }, [qrCode]);

    const fetchTableInfo = async () => {
        try {
            setLoading(true);
            setError(null);

            const sessionToken = qrCode ? localStorage.getItem(`table_session_${qrCode}`) : null;
            const headers: Record<string, string> = {};
            if (sessionToken) {
                headers['x-table-session'] = sessionToken;
            }

            const response = await apiClient.get(`/table/${qrCode}`, { headers });
            setTable(response.data.table);
        } catch (err: any) {
            console.error('Error fetching table:', err);
            if (err.response?.status === 403) {
                setError(t("tableOccupiedByAnother"));
            } else {
                setError(t("invalidQRCode"));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageSelect = (lang: 'en' | 'ar') => {
        setLanguage(lang);
    };

    const handleProceed = () => {
        navigate(`/table/${qrCode}/menu`);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">{t("loading")}</p>
                </div>
            </div>
        );
    }

    if (error || !table) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
                    <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Utensils className="w-10 h-10 text-red-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t("error")}</h2>
                    <p className="text-gray-600 dark:text-gray-400">{error || t("tableNotAvailable")}</p>
                </div>
            </div>
        );
    }

    const restaurantName = language === 'ar' && table.restaurant.nameAr ? table.restaurant.nameAr : table.restaurant.name;

    return (
        <div
            className={`min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex flex-col items-center justify-center p-6 ${isRTL ? 'rtl' : 'ltr'}`}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
                {/* Restaurant Logo */}
                <div className="mb-6">
                    {table.restaurant.logo ? (
                        <img
                            src={table.restaurant.logo}
                            alt={restaurantName}
                            className="w-28 h-28 rounded-2xl object-cover mx-auto shadow-lg border-4 border-white dark:border-gray-700"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div
                        className="w-28 h-28 rounded-2xl bg-red-100 dark:bg-red-900/30 items-center justify-center mx-auto shadow-lg"
                        style={{ display: table.restaurant.logo ? 'none' : 'flex' }}
                    >
                        <Utensils className="w-14 h-14 text-red-600" />
                    </div>
                </div>

                {/* Restaurant Name */}
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {restaurantName}
                </h1>

                {/* Table Number */}
                <div className="inline-flex items-center gap-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-4 py-2 rounded-full mb-8">
                    <span className="text-sm font-medium">{t("table")}</span>
                    <span className="text-lg font-bold">{table.tableNumber}</span>
                </div>

                {/* Language Selection */}
                <div className="mb-8">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t("selectLanguage")}</p>
                    <div className="flex justify-center gap-4">
                        {/* English Flag */}
                        <button
                            onClick={() => handleLanguageSelect('en')}
                            className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all ${language === 'en'
                                    ? 'bg-red-50 dark:bg-red-900/20 ring-2 ring-red-500'
                                    : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                                }`}
                        >
                            <div className="w-12 h-12 rounded-full overflow-hidden shadow-md border-2 border-white dark:border-gray-600">
                                <svg viewBox="0 0 60 30" className="w-full h-full">
                                    <clipPath id="s">
                                        <path d="M0,0 v30 h60 v-30 z" />
                                    </clipPath>
                                    <clipPath id="t">
                                        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
                                    </clipPath>
                                    <g clipPath="url(#s)">
                                        <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
                                        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
                                        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" />
                                        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
                                        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
                                    </g>
                                </svg>
                            </div>
                            <span className={`text-sm font-medium ${language === 'en' ? 'text-red-600' : 'text-gray-600 dark:text-gray-300'}`}>
                                English
                            </span>
                        </button>

                        {/* Arabic Flag (Saudi Arabia) */}
                        <button
                            onClick={() => handleLanguageSelect('ar')}
                            className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all ${language === 'ar'
                                    ? 'bg-red-50 dark:bg-red-900/20 ring-2 ring-red-500'
                                    : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                                }`}
                        >
                            <div className="w-12 h-12 rounded-full overflow-hidden shadow-md border-2 border-white dark:border-gray-600 bg-[#006C35] flex items-center justify-center">
                                <svg viewBox="0 0 60 40" className="w-full h-full">
                                    <rect width="60" height="40" fill="#006C35" />
                                    <text x="30" y="22" textAnchor="middle" fill="white" fontSize="8" fontFamily="Arial">العربية</text>
                                </svg>
                            </div>
                            <span className={`text-sm font-medium ${language === 'ar' ? 'text-red-600' : 'text-gray-600 dark:text-gray-300'}`}>
                                العربية
                            </span>
                        </button>
                    </div>
                </div>

                {/* Proceed Button */}
                <button
                    onClick={handleProceed}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-6 rounded-2xl transition-colors shadow-lg shadow-red-600/30"
                >
                    {t("viewMenu")}
                </button>

                {/* Powered by */}
                <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
                    Powered by Turbo Delivery
                </p>
            </div>
        </div>
    );
};

export default TableLandingPage;
